<template lang="pug">
.share-popover(
  v-if="visible"
  :class="{ 'share-popover--above': openAbove, 'share-popover--left': openLeft }"
  :style="positionStyle"
  role="dialog"
  aria-modal="true"
  aria-label="Share schedule")
  .share-popover__pointer
  .share-popover__panel
    .share-popover__copy-row
      input.share-popover__url-input(
        ref="urlInput"
        :value="shareUrl"
        readonly
        @focus="$event.target.select()")
      button.share-popover__copy-btn(type="button" @click="copyUrl")
        span(v-if="copied") Copied!
        span(v-else) Copy
</template>

<script>
const POPOVER_WIDTH = 320;
const POPOVER_OFFSET_Y = 12;

export default {
  name: 'SharePopover',

  props: {
    visible: { type: Boolean, default: false },
    triggerEl: { type: [Object, Element], default: null },
  },

  data() {
    return {
      top: 0,
      left: 0,
      openAbove: false,
      openLeft: false,
      copied: false,
      copiedTimer: null,
      shareUrl: '',
    };
  },

  computed: {
    positionStyle() {
      return {
        position: 'fixed',
        top: `${this.top}px`,
        left: `${this.left}px`,
        width: `${POPOVER_WIDTH}px`,
        zIndex: 11000,
      };
    },
  },

  watch: {
    visible(now) {
      if (now) {
        this.shareUrl = window.location.href;
        this.copied = false;
        if (this.copiedTimer) { clearTimeout(this.copiedTimer); this.copiedTimer = null; }
        this.attachListeners();
      } else {
        this.detachListeners();
        if (this.copiedTimer) { clearTimeout(this.copiedTimer); this.copiedTimer = null; }
      }
    },
    triggerEl() {
      if (this.visible) this.$nextTick(() => this.computePosition());
    },
  },

  beforeDestroy() {
    this.detachListeners();
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  },

  methods: {
    computePosition() {
      if (!this.triggerEl) return;
      const r = this.triggerEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const popW = POPOVER_WIDTH;
      const popH = (this.$el && this.$el.offsetHeight) || 80;

      const spaceBelow = vh - r.bottom - POPOVER_OFFSET_Y;
      const spaceAbove = r.top - POPOVER_OFFSET_Y;
      this.openAbove = spaceBelow < popH && spaceAbove >= popH;

      const top = this.openAbove
        ? r.top - POPOVER_OFFSET_Y - popH
        : r.bottom + POPOVER_OFFSET_Y;

      const rightAligned = r.right - popW;
      this.openLeft = rightAligned < 8;
      const rawLeft = this.openLeft ? r.left : rightAligned;

      this.top = top;
      this.left = Math.max(8, Math.min(vw - popW - 8, rawLeft));
    },

    attachListeners() {
      this.$nextTick(() => {
        this.computePosition();
        this.$refs.urlInput?.focus();
      });
      document.addEventListener('mousedown', this.onOutsideClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.computePosition, true);
      window.addEventListener('resize', this.close);
    },

    detachListeners() {
      document.removeEventListener('mousedown', this.onOutsideClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.computePosition, true);
      window.removeEventListener('resize', this.close);
    },

    onOutsideClick(e) {
      if (!this.$el || !this.$el.contains) return;
      if (this.$el === e.target || this.$el.contains(e.target)) return;
      if (this.triggerEl && this.triggerEl.contains(e.target)) return;
      this.close();
    },

    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    },

    close() {
      this.triggerEl?.focus();
      this.$emit('close');
    },

    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.copied = true;
        if (this.copiedTimer) clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => {
          this.copied = false;
          this.copiedTimer = null;
        }, 2000);
      } catch {
        this.$refs.urlInput?.select();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.share-popover {
  pointer-events: auto;
  font-family: 'Public Sans', sans-serif;
  display: flex;
  flex-direction: column;
}

.share-popover__pointer {
  width: 0;
  height: 0;
  margin-left: auto;
  margin-right: 16px;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
  filter: drop-shadow(0 -1px 1px rgba(22, 56, 116, 0.08));
}

.share-popover--above {
  .share-popover__pointer {
    order: 1;
    border-bottom: none;
    border-top: 10px solid white;
    filter: drop-shadow(0 1px 1px rgba(22, 56, 116, 0.08));
  }

  .share-popover__panel {
    order: 0;
  }
}

.share-popover--left {
  .share-popover__pointer {
    margin-left: 16px;
    margin-right: auto;
  }
}

.share-popover__panel {
  background: white;
  border: 1px solid $color-border;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 16px 24px -6px rgba(22, 56, 116, 0.14), 0 8px 10px -6px rgba(22, 56, 116, 0.1);
}

.share-popover__copy-row {
  display: flex;
  gap: 8px;
}

.share-popover__url-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid $color-border;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Public Sans', sans-serif;
  color: $color-text-dark;
  background: $color-surface-hover;
  outline: none;
  min-width: 0;

  &:focus {
    border-color: $color-primary;
  }
}

.share-popover__copy-btn {
  padding: 8px 14px;
  background: $color-cta-accent;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Public Sans', sans-serif;
  color: $color-primary;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: $shadow-btn-subtle;

  &:hover {
    background: $color-cta-accent-hover;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}
</style>
